import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  Switch,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  buildTableRowsPerPageOptions,
  TABLE_INITIAL_ROWS_PER_PAGE,
  TABLE_HEADER_FONT_SIZE,
} from "../Table/constants.js";

// IMPORT YOUR SERVICE (adjust the path for your project)
import BrokerTreesService from "../../Endpoints/BrokerTrees";
import brokersService from "../../Endpoints/BrokersIBs";
import EntitiesService from "../../Endpoints/SettingEntities";
import TradingGroupsService from "../../Endpoints/TradingGroups";

const RedConnector = styled(StepConnector)(() => ({
  top: 13,

  "& .MuiStepConnector-line": {
    borderTopWidth: 3,
    borderColor: "#E5E7EB",
    borderRadius: 999,
  },

  "&.Mui-active .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },

  "&.Mui-completed .MuiStepConnector-line": {
    borderColor: "#DC2626",
  },
}));

function RedStepIcon(props) {
  const { active, completed, icon } = props;
  const isOn = active || completed;

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        fontWeight: 900,
        color: isOn ? "white" : "#111827",
        background: isOn ? "#DC2626" : "#E5E7EB",
      }}
    >
      {icon}
    </Box>
  );
}

function flattenTradingGroups(apiPayload) {
  const groups = Array.isArray(apiPayload?.groups) ? apiPayload.groups : [];

  return groups.map((group, index) => ({
    id: group?.id ?? index + 1,
    group: String(group?.group ?? "").trim(),
    server: String(group?.server ?? "").trim(),
    currency: String(group?.currency ?? "").trim(),
    leverage: group?.leverage ?? "",
    mcso: String(group?.mcso ?? "").trim(),
    groupSplit: Array.isArray(group?.group_split) ? group.group_split : [],
  }));
}

const GROUP_TABLE_COLUMNS = [
  {
    field: "select",
    label: "",
    minWidth: 52,
    width: 52,
    align: "center",
    resizable: false,
  },
  { field: "group", label: "Group", minWidth: 170, width: 190 },
  { field: "server", label: "Server", minWidth: 130, width: 150 },
  { field: "currency", label: "Currency", minWidth: 110, width: 120 },
  { field: "leverage", label: "Leverage", minWidth: 110, width: 120 },
  { field: "mcso", label: "MCSO", minWidth: 110, width: 120 },
];

const SPLIT_TABLE_COLUMNS = [
  {
    field: "subowner",
    label: "List of IBs",
    minWidth: 190,
    width: 240,
  },
  {
    field: "shares",
    label: "Shares %",
    minWidth: 110,
    width: 120,
    align: "right",
  },
  {
    field: "permissions",
    label: "Permissions",
    minWidth: 220,
    width: 280,
    align: "center",
  },
  {
    field: "action",
    label: "Action",
    minWidth: 90,
    width: 100,
    align: "right",
  },
];

const buildInitialColumnWidths = (columns) =>
  columns.reduce((widths, column) => {
    widths[column.field] = column.width;
    return widths;
  }, {});

const initialGroupTableColumnWidths = buildInitialColumnWidths(GROUP_TABLE_COLUMNS);
const initialSplitTableColumnWidths = buildInitialColumnWidths(SPLIT_TABLE_COLUMNS);

function useResizableColumns(columns, initialWidths) {
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  const tableMinWidth = useMemo(() => {
    return columns.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columns, columnWidths]);

  const columnCellSx = useMemo(() => {
    return columns.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columns, columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      if (column.resizable === false) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    if (column.resizable === false) return;

    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  return {
    columnWidths,
    tableMinWidth,
    columnCellSx,
    handleColumnResizeStart,
    handleColumnResizeKeyDown,
  };
}

/* =========================
   PAGE
========================= */
function CreateTree() {
  const navigate = useNavigate();

  const steps = ["Tree set-up", "Group selection", "Splits"];
  const [activeStep, setActiveStep] = useState(0);

  // submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // success modal
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [createdTreeId, setCreatedTreeId] = useState(null);

  // Step 1 (main form)
  const [form, setForm] = useState({
    accountStatus: true,
    ownerBrokerId: "",
    treeName: "",
    entity: "",
    language: "",
    masterIbId: "", // numeric string (optional)
    ownershipDistribution: "FULL", // FULL | SUB
  });

  // Step 1 secondary screen (SUB flow)
  const [subOwnersMode, setSubOwnersMode] = useState(false);

  const [brokerOptions, setBrokerOptions] = useState([]);
  const [ibOptions, setIbOptions] = useState([]);
  const [brokersLoading, setBrokersLoading] = useState(false);
  const [brokersError, setBrokersError] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);

  // IB owners (multi-select) - uses the same enabled IB list as Master IB
  const [ibOwnerIds, setIbOwnerIds] = useState([]);

  // Ownership state
  const [brokerShare, setBrokerShare] = useState(""); // string for typing
  const brokerShareInputRef = useRef(null);

  // sub owners
  const [subDraft, setSubDraft] = useState({ userId: "", shares: "" });
  const [subOwners, setSubOwners] = useState([]); // [{rowId,userId,shares}]
  const [editingSubId, setEditingSubId] = useState(null);

  // Step 2 state
  const [groupSearch, setGroupSearch] = useState("");
  const [serverFilter, setServerFilter] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupPage, setGroupPage] = useState(1);
  const GROUP_ROWS_OPTIONS = useMemo(
    () => buildTableRowsPerPageOptions([5, 7, 10, 25, 50, 100]),
    []
  );
  const [groupRowsPerPage, setGroupRowsPerPage] = useState(
    TABLE_INITIAL_ROWS_PER_PAGE
  );
  const [tradingGroups, setTradingGroups] = useState([]);
  const [tradingGroupsLoading, setTradingGroupsLoading] = useState(false);
  const [tradingGroupsError, setTradingGroupsError] = useState("");
  const [focusedGroupId, setFocusedGroupId] = useState(null);

  // Step 3 state
  const [splitDraft, setSplitDraft] = useState({
    userId: "",
    userName: "",
    shares: "",
    showLink: false,
    seeClients: false,
    seeFull: false,
  });
  const [splits, setSplits] = useState([]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));


  const toInt = (v) => {
    const n = Number(String(v ?? "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const normalizeBrokerStatus = (value) => {
    const normalized = String(value ?? "").trim().toUpperCase();
    if (normalized === "ENABLED") return "ACTIVE";
    if (normalized === "DISABLED") return "INACTIVE";
    return normalized;
  };

  const normalizeUserType = (value) =>
    String(value ?? "").trim().toUpperCase();

  const isBrokerUser = (value) => normalizeUserType(value) === "BROKER";

  const isIbUser = (value) => {
    const normalized = normalizeUserType(value);

    return (
      normalized === "IB" ||
      normalized === "INTRODUCING_BROKER" ||
      normalized === "INTRODUCER" ||
      normalized === "INTRODUCINGBROKER"
    );
  };

  useEffect(() => {
    let alive = true;

    const loadEnabledUsers = async () => {
      setBrokersLoading(true);
      setBrokersError("");

      try {
        const response = await brokersService.BrokersIBs();
        const users = Array.isArray(response?.data?.data?.users)
          ? response.data.data.users
          : [];

        const enabledUsers = users
          .map((user) => ({
            id: user?.id ?? "",
            name: user?.name ?? "",
            email: user?.email ?? "",
            userType: normalizeUserType(
              user?.user_type ?? user?.userType ?? user?.type ?? ""
            ),
            status: normalizeBrokerStatus(
              user?.account_status ?? user?.status ?? user?.status_backend ?? ""
            ),
          }))
          .filter((user) => toInt(user.id))
          .filter((user) => user.status === "ACTIVE")
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          );

        if (!alive) return;

        setBrokerOptions(enabledUsers.filter((user) => isBrokerUser(user.userType)));
        setIbOptions(enabledUsers.filter((user) => isIbUser(user.userType)));
      } catch (error) {
        if (!alive) return;

        setBrokerOptions([]);
        setIbOptions([]);
        setBrokersError("We couldn't load enabled brokers.");
      } finally {
        if (alive) setBrokersLoading(false);
      }
    };

    loadEnabledUsers();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadTradingGroups = async () => {
      setTradingGroupsLoading(true);
      setTradingGroupsError("");

      try {
        const response = await TradingGroupsService.getTradingGroups();
        if (!response) throw new Error("We couldn't load trading groups.");

        const payload = response?.data?.data ?? response?.data ?? {};
        const rows = flattenTradingGroups(payload).filter((row) => row.group);

        if (!alive) return;
        setTradingGroups(rows);
      } catch (error) {
        if (!alive) return;
        setTradingGroups([]);
        setTradingGroupsError("We couldn't load trading groups.");
      } finally {
        if (alive) setTradingGroupsLoading(false);
      }
    };

    loadTradingGroups();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadEntities = async () => {
      try {
        const response = await EntitiesService.getEntities();
        const payload = response?.data?.data ?? response?.data ?? {};
        const entities = Array.isArray(payload?.entities) ? payload.entities : [];
        const names = Array.from(
          new Set(
            entities
              .map((entity) => String(entity?.name ?? "").trim())
              .filter(Boolean)
          )
        ).sort((left, right) =>
          left.localeCompare(right, undefined, { sensitivity: "base" })
        );

        if (!alive) return;
        setEntityOptions(names);
      } catch (error) {
        if (!alive) return;
        setEntityOptions([]);
      }
    };

    loadEntities();

    return () => {
      alive = false;
    };
  }, []);

  const brokerOwnerName = useMemo(() => {
    const selectedBroker = brokerOptions.find(
      (broker) => String(broker.id) === String(form.ownerBrokerId)
    );

    return selectedBroker?.name || "Select a broker";
  }, [brokerOptions, form.ownerBrokerId]);

  const selectedBrokerOption = useMemo(() => {
    return (
      brokerOptions.find(
        (broker) => String(broker.id) === String(form.ownerBrokerId)
      ) ?? null
    );
  }, [brokerOptions, form.ownerBrokerId]);

  const selectedMasterIbOption = useMemo(() => {
    return (
      ibOptions.find((ib) => String(ib.id) === String(form.masterIbId)) ?? null
    );
  }, [ibOptions, form.masterIbId]);

  const selectedSubownerOption = useMemo(() => {
    return (
      brokerOptions.find((broker) => String(broker.id) === String(subDraft.userId)) ?? null
    );
  }, [brokerOptions, subDraft.userId]);

  const selectedSplitDraftOption = useMemo(() => {
    return ibOptions.find((ib) => String(ib.id) === String(splitDraft.userId)) ?? null;
  }, [ibOptions, splitDraft.userId]);

  const formatIbOptionLabel = (option) => {
    if (!option) return "";
    const name = String(option?.name ?? "").trim();
    const id = String(option?.id ?? "").trim();
    if (name && id) return `${name} (${id})`;
    return name || id;
  };

  // "IB Owners" reuses the same enabled IB list already loaded for Master IB.
  // The Autocomplete needs the actual option objects (not just ids) to know
  // which chips/checkboxes are selected, so we derive them from ibOwnerIds.
  const selectedIbOwnerOptions = useMemo(
    () =>
      ibOptions.filter((ib) => ibOwnerIds.includes(String(ib.id))),
    [ibOptions, ibOwnerIds]
  );

  const {
    columnWidths: groupColumnWidths,
    tableMinWidth: groupTableMinWidth,
    columnCellSx: groupColumnCellSx,
    handleColumnResizeStart: handleGroupColumnResizeStart,
    handleColumnResizeKeyDown: handleGroupColumnResizeKeyDown,
  } = useResizableColumns(GROUP_TABLE_COLUMNS, initialGroupTableColumnWidths);

  const {
    columnWidths: splitTableColumnWidths,
    tableMinWidth: splitTableMinWidth,
    columnCellSx: splitTableColumnCellSx,
    handleColumnResizeStart: handleSplitTableColumnResizeStart,
    handleColumnResizeKeyDown: handleSplitTableColumnResizeKeyDown,
  } = useResizableColumns(SPLIT_TABLE_COLUMNS, initialSplitTableColumnWidths);

  /* =========================
     STEP 1 BASIC VALIDATION
  ========================= */
  const canContinueStep1Basic =
    String(form.ownerBrokerId).trim() &&
    String(form.treeName).trim() &&
    String(form.entity).trim() &&
    String(form.language).trim();

  /* =========================
     STEP 2 VALIDATION
  ========================= */
  const canContinueStep2 = selectedGroupIds.length > 0;

  /* ---------- Step 2 filtering/pagination ---------- */
  const filteredGroups = useMemo(() => {
    const s = groupSearch.toLowerCase().trim();
    return tradingGroups.filter((g) => {
      const okSearch =
        !s ||
        [g.group, g.server, g.currency, g.leverage, g.mcso]
          .map((value) => String(value ?? "").toLowerCase())
          .some((value) => value.includes(s));
      const okServer = !serverFilter || g.server === serverFilter;
      return okSearch && okServer;
    });
  }, [tradingGroups, groupSearch, serverFilter]);

  const paginatedGroups = useMemo(() => {
    const start = (groupPage - 1) * groupRowsPerPage;
    return filteredGroups.slice(start, start + groupRowsPerPage);
  }, [filteredGroups, groupPage, groupRowsPerPage]);

  const focusedGroup = useMemo(
    () => tradingGroups.find((group) => group.id === focusedGroupId) ?? null,
    [tradingGroups, focusedGroupId]
  );

  const groupPageCount = Math.max(1, Math.ceil(filteredGroups.length / groupRowsPerPage));

  const groupPageItems = useMemo(() => {
    const last = groupPageCount;
    const current = groupPage;

    if (last <= 4) return Array.from({ length: last }, (_, index) => index + 1);
    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];

    return ["…", current - 1, current, current + 1, "…"];
  }, [groupPageCount, groupPage]);

  const serverOptions = useMemo(() => {
    const normalizedMap = new Map();

    tradingGroups.forEach((group) => {
      const rawServer = String(group?.server ?? "").trim();
      if (!rawServer) return;

      const normalizedKey = rawServer.toLowerCase();
      if (!normalizedMap.has(normalizedKey)) {
        normalizedMap.set(normalizedKey, rawServer);
      }
    });

    return Array.from(normalizedMap.values()).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" })
    );
  }, [tradingGroups]);

  const toggleGroup = (id) => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleGroupClick = (id) => {
    const isActiveSelection =
      focusedGroupId === id && selectedGroupIds.includes(id);

    setFocusedGroupId(isActiveSelection ? null : id);
    toggleGroup(id);
  };

  useEffect(() => {
    setGroupPage((prev) => Math.min(prev, groupPageCount));
  }, [groupPageCount]);

  const groupArrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  /* =========================
     Step 1 SUB FLOW
  ========================= */
  const brokerShareNum = useMemo(() => {
    const raw = String(brokerShare).replace("%", "").trim();
    if (!raw) return 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, n);
  }, [brokerShare]);

  const subOwnersTotal = useMemo(() => {
    return subOwners.reduce((sum, s) => sum + (Number(s.shares) || 0), 0);
  }, [subOwners]);

  const draftSharesNum = useMemo(() => {
    const value = Number(String(subDraft.shares).replace("%", "").trim());
    return Number.isFinite(value) && value > 0 ? value : 0;
  }, [subDraft.shares]);

  const effectiveSubOwnersTotal = useMemo(() => {
    if (!subOwnersMode || form.ownershipDistribution !== "SUB") return subOwnersTotal;

    if (editingSubId) {
      const current = subOwners.find((x) => x.rowId === editingSubId);
      const currentShares = current ? Number(current.shares) || 0 : 0;
      return Math.max(0, subOwnersTotal - currentShares + draftSharesNum);
    }

    return Math.max(0, subOwnersTotal + draftSharesNum);
  }, [
    subOwnersMode,
    form.ownershipDistribution,
    subOwnersTotal,
    editingSubId,
    subOwners,
    draftSharesNum,
  ]);

  const totalShareCount = useMemo(() => {
    const t = brokerShareNum + subOwnersTotal;
    return Math.max(0, t);
  }, [brokerShareNum, subOwnersTotal]);

  const remainingShare = useMemo(() => {
    return Math.max(0, 100 - (brokerShareNum + subOwnersTotal));
  }, [brokerShareNum, subOwnersTotal]);

  const canAddSubowner = useMemo(() => {
    if (form.ownershipDistribution === "FULL") return false;

    const idOk = !!toInt(subDraft.userId);
    const sharesNum = Number(String(subDraft.shares).replace("%", "").trim());
    const sharesOk = Number.isFinite(sharesNum) && sharesNum > 0;
    if (!idOk || !sharesOk) return false;

    if (editingSubId) {
      const current = subOwners.find((x) => x.rowId === editingSubId);
      const currentShares = current ? Number(current.shares) || 0 : 0;
      const wouldTotal = (subOwnersTotal - currentShares) + sharesNum;
      return wouldTotal <= 100;
    }

    return subOwnersTotal + sharesNum <= 100;
  }, [
    form.ownershipDistribution,
    subDraft.userId,
    subDraft.shares,
    subOwnersTotal,
    editingSubId,
    subOwners,
  ]);

  const upsertSubowner = () => {
    if (!canAddSubowner) return;

    const userId = toInt(subDraft.userId);
    const shares = Number(String(subDraft.shares).replace("%", "").trim());
    if (!userId) return;
    const matchedIb = ibOptions.find((ib) => Number(ib.id) === Number(userId));
    const userName = String(matchedIb?.name ?? "").trim();

    if (editingSubId) {
      setSubOwners((prev) =>
        prev.map((x) =>
          x.rowId === editingSubId ? { ...x, userId, userName, shares } : x
        )
      );
      setEditingSubId(null);
    } else {
      setSubOwners((prev) => [
        ...prev,
        {
          rowId:
            (typeof crypto !== "undefined" && crypto?.randomUUID?.()) ||
            String(Date.now()),
          userId,
          userName,
          shares,
        },
      ]);
    }

    setSubDraft({ userId: "", shares: "" });
  };

  const onEditSubowner = (rowId) => {
    const row = subOwners.find((x) => x.rowId === rowId);
    if (!row) return;
    setEditingSubId(rowId);
    setSubDraft({ userId: String(row.userId), shares: String(row.shares) });
  };

  const onDeleteSubowner = (rowId) => {
    setSubOwners((prev) => prev.filter((x) => x.rowId !== rowId));
    if (editingSubId === rowId) {
      setEditingSubId(null);
      setSubDraft({ userId: "", shares: "" });
    }
  };

  const canContinueStep1 = useMemo(() => {
    if (!subOwnersMode) return !!canContinueStep1Basic;
    return canContinueStep1Basic && totalShareCount === 100;
  }, [subOwnersMode, canContinueStep1Basic, totalShareCount]);

  /* =========================
     STEP 3 calculations
  ========================= */
  const totalSharesStep3 = useMemo(() => {
    return splits.reduce((sum, s) => sum + (Number(s.shares) || 0), 0);
  }, [splits]);

  const remainingStep3 = Math.max(0, 100 - totalSharesStep3);
  const canCreateTreeStep3 = remainingStep3 >= 0 && remainingStep3 <= 100;

  const canAddSplit = useMemo(() => {
    const userIdOk = !!toInt(splitDraft.userId);
    const sharesNum = Number(splitDraft.shares);
    const sharesOk = Number.isFinite(sharesNum) && sharesNum >= 0 && sharesNum <= remainingStep3;
    return userIdOk && sharesOk;
  }, [splitDraft.userId, splitDraft.shares, remainingStep3]);

  const handleAddSplit = () => {
    if (!canAddSplit) return;

    const userId = toInt(splitDraft.userId);
    if (!userId) return;
    const matchedIb = ibOptions.find((ib) => Number(ib.id) === Number(userId));
    const userName = String(matchedIb?.name ?? "").trim();

    setSplits((prev) => [
      ...prev,
      {
        rowId:
          (typeof crypto !== "undefined" && crypto?.randomUUID?.()) ||
          String(Date.now()),
        userId,
        userName,
        shares: Number(splitDraft.shares),
        showLink: !!splitDraft.showLink,
        seeClients: !!splitDraft.seeClients,
        seeFull: !!splitDraft.seeFull,
      },
    ]);

    setSplitDraft({
      userId: "",
      userName: "",
      shares: "",
      showLink: false,
      seeClients: false,
      seeFull: false,
    });
  };

  const handleDeleteSplit = (rowId) => {
    setSplits((prev) => prev.filter((x) => x.rowId !== rowId));
  };

  const toggleDraft = (k) => setSplitDraft((p) => ({ ...p, [k]: !p[k] }));

  /* =========================
     NAV
  ========================= */
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const enterSubOwnersMode = () => {
    setSubOwnersMode(true);
    setBrokerShare("100");
    setSubOwners([]);
    setSubDraft({ userId: "", shares: "" });
    setEditingSubId(null);
  };

  useEffect(() => {
    if (!subOwnersMode) return;
    if (form.ownershipDistribution !== "SUB") return;

    const remainingOwnerShare = Math.max(0, 100 - effectiveSubOwnersTotal);
    setBrokerShare(String(remainingOwnerShare));
  }, [subOwnersMode, form.ownershipDistribution, effectiveSubOwnersTotal]);

  const handleContinue = () => {
    if (activeStep === 0) {
      if (!canContinueStep1Basic) return;

      if (!subOwnersMode) {
        enterSubOwnersMode();
        return;
      }

      if (subOwnersMode && !canContinueStep1) return;

      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      if (!canContinueStep2) return;
      setActiveStep(2);
      return;
    }
  };

  /* =========================
     BUILD PAYLOAD + POST
  ========================= */
  const buildCreateTreePayload = () => {
    const ownerBrokerId = toInt(form.ownerBrokerId);
    const masterIbId = toInt(form.masterIbId); // optional

    // "IB Owners" multi-select -> [{ id: <ibId> }, ...]
    const ibOwnersPayload = ibOwnerIds
      .map((id) => ({ id: toInt(id) }))
      .filter((owner) => !!owner.id);

    const ownersPayload =
      form.ownershipDistribution === "SUB"
        ? subOwners.map((o) => ({
            user: { id: o.userId },
            percentage: Number(o.shares) || 0,
          }))
        : [];

    const splitsPayload = splits.map((s) => ({
      user: { id: s.userId },
      percentage: Number(s.shares) || 0,
      client_view_flag: !!s.seeClients,
      referral_link_flag: !!s.showLink,
      show_full_flag: !!s.seeFull,
    }));

    const selectedGroup = tradingGroups.find((group) =>
      selectedGroupIds.includes(group.id)
    );

    return {
      tree_name: String(form.treeName).trim(),
      ...(ownerBrokerId ? { owner: { id: ownerBrokerId } } : {}),
      ...(masterIbId ? { master_ib: { id: masterIbId } } : {}),
      ib_owners: ibOwnersPayload,
      group: selectedGroup?.group || "",
      server: selectedGroup?.server || "MT4 LTD Demo",
      entity: String(form.entity).trim(),
      language: String(form.language).trim(),
      status: !!form.accountStatus,
      splits: splitsPayload,
      owners: ownersPayload,
    };
  };

  const handleCreateTree = async () => {
    setSubmitError("");

    try {
      const payload = buildCreateTreePayload();

      setIsSubmitting(true);
      const res = await BrokerTreesService.createBrokerTree(payload);
      if (!res) {
        setSubmitError("Error creating tree");
        return;
      }

      // try to get tree_id from several typical locations
      const treeId =
        res?.data?.data?.tree_id ??
        res?.data?.tree_id ??
        res?.data?.data?.id ??
        res?.data?.id ??
        null;

      setCreatedTreeId(treeId);
      setOpenSuccessModal(true);
    } catch (e) {
      console.error(e);
      setSubmitError(e?.message || "Error creating tree");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- UI helpers ---------- */
  const cardSx = {
    maxWidth: 920,
    mx: "auto",
    background: "white",
    border: "1px solid #E5E7EB",
    borderRadius: 3,
    p: { xs: 2, md: 3 },
  };

  const innerPanelSx = {
    border: "1px solid #EEF2F7",
    borderRadius: 3,
    p: 2,
    background: "white",
    boxShadow: "0 12px 30px rgba(17, 24, 39, 0.06)",
  };

  const greenCheckboxSx = {
    color: "#111827",
    "&.Mui-checked": { color: "#15803D" },
    "&.MuiCheckbox-indeterminate": { color: "#15803D" },
  };

  const addBtnSx = (enabled) => ({
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 2,
    background: enabled ? "#DC2626" : "#E5E7EB",
    color: enabled ? "white" : "#111827",
    "&:hover": { background: enabled ? "#B91C1C" : "#D1D5DB" },
  });

  const blackBtnSx = {
    textTransform: "none",
    fontWeight: 800,
    borderRadius: 2,
    background: "#FFFFFF",
    color: "#111827",
    borderColor: "#111827",
    boxShadow: "none",
    "&:hover": {
      background: "#F9FAFB",
      borderColor: "#030712",
      color: "#030712",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      background: "#FFFFFF",
      color: "#9CA3AF",
      borderColor: "#E5E7EB",
    },
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 4,
        pt: 2,
        pb: 4,
        background:
          "radial-gradient(900px 500px at 90% 60%, rgba(59,130,246,.18), transparent 60%), radial-gradient(700px 400px at 10% 70%, rgba(236,72,153,.16), transparent 55%), #F9FAFB",
      }}
    >
      {/* Go back */}
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate(-1)}
        sx={{ textTransform: "none", mb: 1.5, color: "#111827" }}
      >
        Go back
      </Button>

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>
          Create a Tree
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#6B7280" }}>
          Set up a new tree with rebate and allocation conditions.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 920, mx: "auto", mb: 2 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<RedConnector />}
          sx={{
            "& .MuiStepLabel-label": { fontSize: 12, color: "#6B7280" },
            "& .MuiStepLabel-label.Mui-active": { color: "#111827", fontWeight: 700 },
            "& .MuiStepLabel-label.Mui-completed": { color: "#111827", fontWeight: 700 },

            ...(activeStep === 0 && {
              "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line": {
                position: "relative",
              },
              "& .MuiStep-root:nth-of-type(2) .MuiStepConnector-line::after": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: "50%",
                height: 3,
                backgroundColor: "#DC2626",
                borderRadius: 999,
                pointerEvents: "none",
              },
            }),
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={RedStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main Card */}
      <Box sx={cardSx}>
        {/* Title row + switch */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: "#111827" }}>
            {activeStep === 0 && subOwnersMode
              ? "Step 1.5: Tree set-up"
              : `Step ${activeStep + 1}: ${steps[activeStep]}`}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>Account status:</Typography>
            <Switch
              checked={form.accountStatus}
              onChange={(e) => setField("accountStatus", e.target.checked)}
            />
          </Box>
        </Box>

        {/* ===================== STEP 1 ===================== */}
        {activeStep === 0 && (
          <>
            {subOwnersMode ? (
              <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
                <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 2 }}>
                  Define the ownership share. The total sum of shares, including the broker owner and
                  the sub-owners, must equal 100%.
                </Typography>

                {/* TOP ROW */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 260px" },
                    gap: 2,
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  {/* Broker owner card */}
                  <Box
                    sx={{
                      border: "1px solid #EEF2F7",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      background: "#F9FAFB",
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background: "#111827",
                        color: "white",
                      }}
                    >
                      <PeopleAltOutlinedIcon fontSize="small" />
                    </Box>

                    <Typography sx={{ fontSize: 13, color: "#374151", fontWeight: 700 }}>
                      Broker owner:&nbsp;
                      <span style={{ color: "#111827" }}>{brokerOwnerName}</span>
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        inputRef={brokerShareInputRef}
                        size="small"
                        value={brokerShare}
                        onChange={(e) => setBrokerShare(e.target.value)}
                        onBlur={() => {
                          if (!String(brokerShare).trim()) setBrokerShare("100");
                        }}
                        placeholder="--% of shares"
                        inputProps={{ inputMode: "numeric" }}
                        sx={{
                          width: 150,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            background: "white",
                            fontWeight: 700,
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => brokerShareInputRef.current?.focus?.()}
                        sx={{ border: "1px solid #E5E7EB", borderRadius: 2, color: "#111827" }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Total share count */}
                  <Box
                    sx={{
                      border: "1px solid #EEF2F7",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      background: "#F9FAFB",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 800 }}>
                      Total share count:
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#111827", fontWeight: 900 }}>
                      {totalShareCount}%
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: 11, color: "#6B7280", textAlign: "center", mb: 2 }}>
                  Remember that the total sum of shares, including yours and the shareholders&apos;,
                  must equal 100%.
                </Typography>

                {form.ownershipDistribution === "FULL" ? (
                  <Typography sx={{ fontSize: 12, color: "#6B7280", textAlign: "center", mb: 2 }}>
                    Full ownership selected. Define the broker owner share before continuing.
                  </Typography>
                ) : null}

                {/* Existing subowners list */}
                {form.ownershipDistribution === "SUB" && subOwners.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {subOwners.map((s) => (
                      <Box
                        key={s.rowId}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 84px" },
                          gap: 2,
                          alignItems: "center",
                          mb: 1.2,
                        }}
                      >
                        <TextField
                          size="small"
                          label="List of IBs"
                          value={
                            String(s.userName || "").trim()
                              ? `${s.userName} (${s.userId})`
                              : String(s.userId)
                          }
                          disabled
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              background: "#FAFAFA",
                            },
                          }}
                        />
                        <TextField
                          size="small"
                          label="Shares %"
                          value={`${s.shares}%`}
                          disabled
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              background: "#FAFAFA",
                            },
                          }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <IconButton
                            onClick={() => onDeleteSubowner(s.rowId)}
                            sx={{ border: "1px solid #E5E7EB", borderRadius: 2, color: "#111827" }}
                            aria-label="delete subowner"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => onEditSubowner(s.rowId)}
                            sx={{ border: "1px solid #E5E7EB", borderRadius: 2, color: "#111827" }}
                            aria-label="edit subowner"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                  </Box>
                )}

                {form.ownershipDistribution === "SUB" && (
                  <>
                    {/* Add subowner inputs */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: 2,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={brokerOptions}
                        value={selectedSubownerOption}
                        loading={brokersLoading}
                        noOptionsText={brokersError || "No enabled broker owners found."}
                        getOptionLabel={formatIbOptionLabel}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.id) === String(value?.id)
                        }
                        onChange={(_, option) =>
                          setSubDraft((p) => ({ ...p, userId: option?.id ? String(option.id) : "" }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="List of broker owners"
                            placeholder="Search broker owner..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          />
                        )}
                      />

                      <TextField
                        size="small"
                        label="Shares %"
                        placeholder="--%"
                        value={subDraft.shares}
                        onChange={(e) => setSubDraft((p) => ({ ...p, shares: e.target.value }))}
                        inputProps={{ inputMode: "numeric" }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={upsertSubowner}
                        disabled={!canAddSubowner}
                        sx={addBtnSx(canAddSubowner)}
                      >
                        {editingSubId ? "Save broker" : "+ Add broker"}
                      </Button>
                    </Box>
                  </>
                )}

                <Typography sx={{ fontSize: 12, color: "#6B7280", textAlign: "center" }}>
                  Remaining to assign: <b style={{ color: "#111827" }}>{remainingShare}%</b>
                </Typography>
              </Box>
            ) : (
              <>
                {/* Main Step 1 form */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                    gap: 2,
                  }}
                >
                  <Autocomplete
                    fullWidth
                    options={brokerOptions}
                    value={selectedBrokerOption}
                    loading={brokersLoading}
                    noOptionsText={
                      brokersLoading ? "Loading brokers..." : "No enabled brokers found."
                    }
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id) === String(value?.id)
                    }
                    onChange={(_, option) =>
                      setField("ownerBrokerId", option?.id ? String(option.id) : "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Broker Owner"
                        placeholder="Search broker..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <TextField
                    size="small"
                    label="Tree name"
                    value={form.treeName}
                    onChange={(e) => setField("treeName", e.target.value)}
                    placeholder="Enter a name for this Tree"
                    fullWidth
                  />
                </Box>

                {brokersError ? (
                  <Typography sx={{ mt: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                    {brokersError}
                  </Typography>
                ) : null}

                {!brokersLoading && !brokersError && brokerOptions.length === 0 ? (
                  <Typography sx={{ mt: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                    No enabled brokers found.
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Entity</InputLabel>
                    <Select
                      label="Entity"
                      value={form.entity}
                      onChange={(e) => setField("entity", e.target.value)}
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      {entityOptions.map((entityName) => (
                        <MenuItem key={entityName} value={entityName}>
                          {entityName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      label="Language"
                      value={form.language}
                      onChange={(e) => setField("language", e.target.value)}
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      <MenuItem value="ENGLISH">ENGLISH</MenuItem>
                      <MenuItem value="SPANISH">SPANISH</MenuItem>
                      <MenuItem value="PORTUGUESE">PORTUGUESE</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Master IB + IB Owners: same enabled-IB dataset, single vs multiple selection */}
                <Box
                  sx={{
                    display: "grid",
                    // `minmax(0, 1fr)` (instead of a plain `1fr`) is required here:
                    // grid items default to `min-width: auto`, so a field whose content
                    // wants more room than its column (e.g. this multi-select once several
                    // IBs are chosen) would otherwise stretch the whole column past the
                    // card and push the field (and its dropdown) off-screen.
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                    gap: 2,
                    mt: 2,
                    alignItems: "start",
                  }}
                >
                  <Autocomplete
                    fullWidth
                    options={ibOptions}
                    value={selectedMasterIbOption}
                    loading={brokersLoading}
                    noOptionsText={brokersLoading ? "Loading IBs..." : "No enabled IBs found."}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id) === String(value?.id)
                    }
                    onChange={(_, option) =>
                      setField("masterIbId", option?.id ? String(option.id) : "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Master IB (optional)"
                        placeholder="Search IB..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {/*
                    IB Owners: same enabled-IB dataset as Master IB, but multiple
                    selection. Uses MUI's Autocomplete (multiple + checkboxes)
                    instead of a customized Select, so it shares the exact same
                    positioning/anchoring behaviour as every other Autocomplete
                    field in this form (Broker Owner, Master IB, etc.) and never
                    runs into the "menu opens off-screen / selection does nothing"
                    issue a custom Select can have inside a CSS grid.
                  */}
                  <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    limitTags={2}
                    options={ibOptions}
                    value={selectedIbOwnerOptions}
                    loading={brokersLoading}
                    disabled={brokersLoading}
                    noOptionsText={brokersLoading ? "Loading IBs..." : "No enabled IBs found."}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id) === String(value?.id)
                    }
                    onChange={(_, selectedOptions) =>
                      setIbOwnerIds(selectedOptions.map((option) => String(option.id)))
                    }
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Box component="li" key={key} {...optionProps}>
                          <Checkbox
                            checked={selected}
                            sx={{ ...greenCheckboxSx, mr: 1, p: 0.5 }}
                          />
                          {formatIbOptionLabel(option)}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="IB Owners (optional)"
                        placeholder={
                          selectedIbOwnerOptions.length ? "" : "Search IB..."
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                    Ownership distribution
                  </Typography>

                  <RadioGroup
                    row
                    value={form.ownershipDistribution}
                    onChange={(e) => setField("ownershipDistribution", e.target.value)}
                    sx={{ gap: 2 }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                      }}
                    >
                      <FormControlLabel
                        value="FULL"
                        control={<Radio />}
                        label="Full ownership of main Broker/IB"
                      />
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                      }}
                    >
                      <FormControlLabel value="SUB" control={<Radio />} label="Sub-owners" />
                    </Box>
                  </RadioGroup>
                </Box>
              </>
            )}
          </>
        )}

        {/* ===================== STEP 2 ===================== */}
        {activeStep === 1 && (
          <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
            {/* Filters row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "220px 1fr 110px" },
                gap: 1.5,
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search by user group name"
                value={groupSearch}
                onChange={(e) => {
                  setGroupSearch(e.target.value);
                  setGroupPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                      <SearchIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />

              <FormControl size="small" fullWidth>
                <InputLabel>Server</InputLabel>
                <Select
                  label="Server"
                  value={serverFilter}
                  onChange={(e) => {
                    setServerFilter(e.target.value);
                    setGroupPage(1);
                  }}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  {serverOptions.map((server) => (
                    <MenuItem key={server} value={server}>
                      {server}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                sx={addBtnSx(true)}
                onClick={() => setGroupPage(1)}
              >
                Search
              </Button>
            </Box>

            {tradingGroupsError ? (
              <Typography sx={{ mb: 1.5, color: "#B91C1C", fontWeight: 800, fontSize: 12 }}>
                {tradingGroupsError}
              </Typography>
            ) : null}

            {/* Trading groups and selected group details */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: focusedGroup
                  ? { xs: "1fr", md: "minmax(0, 3fr) minmax(360px, 2fr)" }
                  : "minmax(0, 1fr)",
                gap: 0,
                alignItems: "stretch",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                borderRadius: 3.5,
                backgroundColor: "#FFFFFF",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <TableContainer
                sx={{
                  borderRadius: 0,
                  overflow: "auto",
                  border: 0,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: groupTableMinWidth,
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      fontFamily: '"Plus Jakarta Sans", sans-serif',
                    },
                    "& .MuiTableBody-root .MuiTableRow-root": {
                      height: 56,
                    },
                    "& .MuiTableBody-root .MuiTableCell-root": {
                      height: 56,
                      boxSizing: "border-box",
                    },
                  }}
                >
                  <colgroup>
                    {GROUP_TABLE_COLUMNS.map((column) => (
                      <col
                        key={column.field}
                        style={{
                          width: `${groupColumnWidths[column.field] || column.width}px`,
                        }}
                      />
                    ))}
                  </colgroup>
                  <TableHead sx={{ position: "sticky", top: 0, zIndex: 2 }}>
                    <TableRow sx={{ background: "#F8FAFC" }}>
                      {GROUP_TABLE_COLUMNS.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.align || "left"}
                          sx={{
                            ...groupColumnCellSx[column.field],
                            height: "50px",
                            minHeight: "50px",
                            maxHeight: "50px",
                            paddingTop: 0,
                            paddingBottom: 0,
                            fontSize: TABLE_HEADER_FONT_SIZE,
                            fontWeight: 800,
                            color: "#334155",
                            backgroundColor: "#F8FAFC",
                            borderBottom: "1px solid #E2E8F0",
                            position: "relative",
                            pr: column.resizable === false ? 2 : "28px",
                            userSelect: "none",
                          }}
                        >
                          <Box
                            component="span"
                            title={column.label}
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {column.label}
                          </Box>
                          {column.resizable !== false && (
                            <Box
                              component="span"
                              role="separator"
                              aria-label={`Resize ${column.label} column`}
                              aria-orientation="vertical"
                              aria-valuemin={column.minWidth}
                              aria-valuenow={groupColumnWidths[column.field] || column.width}
                              tabIndex={0}
                              onPointerDown={(event) =>
                                handleGroupColumnResizeStart(event, column)
                              }
                              onKeyDown={(event) =>
                                handleGroupColumnResizeKeyDown(event, column)
                              }
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: "12px",
                                height: "100%",
                                cursor: "col-resize",
                                touchAction: "none",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  top: "22%",
                                  bottom: "22%",
                                  left: "50%",
                                  width: "2px",
                                  transform: "translateX(-50%)",
                                  borderRadius: "2px",
                                  backgroundColor: "rgba(21, 32, 42, 0.22)",
                                },
                                "&:hover::after, &:focus-visible::after": {
                                  backgroundColor: "#15202A",
                                },
                                "&:focus-visible": {
                                  outline: "2px solid #15202A",
                                  outlineOffset: "-2px",
                                },
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                  {tradingGroupsLoading ? (
                    <TableRow>
                      <TableCell colSpan={GROUP_TABLE_COLUMNS.length}>
                        <Box
                          sx={{
                            py: 2,
                            textAlign: "center",
                            color: "#6B7280",
                            fontSize: 12,
                          }}
                        >
                          Loading trading groups...
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!tradingGroupsLoading && paginatedGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={GROUP_TABLE_COLUMNS.length}>
                        <Box
                          sx={{
                            py: 2,
                            textAlign: "center",
                            color: "#6B7280",
                            fontSize: 12,
                          }}
                        >
                          No trading groups found.
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {paginatedGroups.map((g) => {
                    const checked = selectedGroupIds.includes(g.id);
                    return (
                      <TableRow
                        key={g.id}
                        hover
                        onClick={() => handleGroupClick(g.id)}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 160ms ease, box-shadow 160ms ease",
                          background:
                            focusedGroupId === g.id
                              ? "#EFF6FF"
                              : checked
                                ? "#EEF6FF"
                                : "white",
                          boxShadow:
                            focusedGroupId === g.id
                              ? "inset 4px 0 0 #15803D"
                              : "none",
                          "&:hover": {
                            backgroundColor:
                              focusedGroupId === g.id ? "#E8F1FF" : "#F8FAFC",
                          },
                        }}
                      >
                        <TableCell sx={groupColumnCellSx.select}>
                          <Checkbox
                            checked={checked}
                            onChange={() => handleGroupClick(g.id)}
                            onClick={(e) => e.stopPropagation()}
                            sx={greenCheckboxSx}
                          />
                        </TableCell>
                        <TableCell
                          title={g.group}
                          sx={{ ...groupColumnCellSx.group, fontSize: 13, color: "#111827" }}
                        >
                          {g.group}
                        </TableCell>
                        <TableCell
                          title={g.server}
                          sx={{ ...groupColumnCellSx.server, fontSize: 13, color: "#111827" }}
                        >
                          {g.server}
                        </TableCell>
                        <TableCell
                          title={g.currency}
                          sx={{
                            ...groupColumnCellSx.currency,
                            fontSize: 13,
                            color: "#111827",
                          }}
                        >
                          {g.currency}
                        </TableCell>
                        <TableCell
                          title={g.leverage}
                          sx={{
                            ...groupColumnCellSx.leverage,
                            fontSize: 13,
                            color: "#111827",
                          }}
                        >
                          {g.leverage}
                        </TableCell>
                        <TableCell
                          title={g.mcso}
                          sx={{ ...groupColumnCellSx.mcso, fontSize: 13, color: "#111827" }}
                        >
                          {g.mcso}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </TableContainer>

              {focusedGroup && (
                <Box
                  sx={{
                    height: { xs: 420, md: 0 },
                    minHeight: { xs: 420, md: "100%" },
                    border: 0,
                    borderLeft: { xs: 0, md: "1px solid #E2E8F0" },
                    borderTop: { xs: "1px solid #E2E8F0", md: 0 },
                    borderRadius: 0,
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0,
                      height: 50,
                      minHeight: 50,
                      maxHeight: 50,
                      flex: "0 0 50px",
                      boxSizing: "border-box",
                      background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
                      borderBottom: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            flexShrink: 0,
                            fontSize: 14,
                            lineHeight: 1,
                            fontWeight: 900,
                            color: "#0F172A",
                          }}
                        >
                          Group split
                        </Typography>
                        <Typography
                          title={focusedGroup.group}
                          sx={{
                            mt: 0,
                            fontSize: 11,
                            lineHeight: 1,
                            fontWeight: 700,
                            color: "#64748B",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {focusedGroup.group}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${focusedGroup.groupSplit.length} ${
                          focusedGroup.groupSplit.length === 1 ? "item" : "items"
                        }`}
                        sx={{
                          flexShrink: 0,
                          height: 22,
                          backgroundColor: "#E2E8F0",
                          color: "#334155",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      />
                    </Box>
                  </Box>

                  {focusedGroup.groupSplit.length > 0 ? (
                    <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
                      <Table
                        size="small"
                        stickyHeader
                        sx={{
                          "& .MuiTableBody-root .MuiTableRow-root": {
                            height: 56,
                          },
                          "& .MuiTableBody-root .MuiTableCell-root": {
                            height: 56,
                            boxSizing: "border-box",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                py: 0,
                                height: 56,
                                minHeight: 56,
                                maxHeight: 56,
                                fontWeight: 800,
                                fontSize: 13,
                                color: "#475569",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              Symbol/security
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 0,
                                height: 56,
                                minHeight: 56,
                                maxHeight: 56,
                                fontWeight: 800,
                                fontSize: 13,
                                color: "#475569",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              Markup
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 0,
                                height: 56,
                                minHeight: 56,
                                maxHeight: 56,
                                fontWeight: 800,
                                fontSize: 13,
                                color: "#475569",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              Rebate
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {focusedGroup.groupSplit.map((split, index) => (
                            <TableRow
                              key={`${focusedGroup.id}-split-${index}`}
                              hover
                              sx={{
                                "&:nth-of-type(even)": { backgroundColor: "#FAFAFA" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 1.35,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "#1E293B",
                                }}
                              >
                                {split?.symbol_or_security ?? ""}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ py: 1.35, fontSize: 13, color: "#475569" }}
                              >
                                {split?.markup ?? ""}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ py: 1.35, fontSize: 13, color: "#475569" }}
                              >
                                {split?.rebate ?? ""}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography sx={{ p: 2, fontSize: 12, color: "#6B7280" }}>
                      This group has no group split details.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.2,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Total:&nbsp;<b style={{ color: "#111827" }}>{filteredGroups.length}</b>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                  Pages:&nbsp;<b style={{ color: "#111827" }}>{groupPageCount}</b>
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

                <Select
                  size="small"
                  value={groupRowsPerPage}
                  onChange={(e) => {
                    setGroupRowsPerPage(Number(e.target.value));
                    setGroupPage(1);
                  }}
                  disabled={tradingGroupsLoading}
                  sx={{
                    height: 32,
                    minWidth: 70,
                    borderRadius: 2,
                    ".MuiSelect-select": { py: 0.5 },
                  }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
                >
                  {GROUP_ROWS_OPTIONS.map((rowsOption) => (
                    <MenuItem key={rowsOption} value={rowsOption}>
                      {rowsOption}
                    </MenuItem>
                  ))}
                </Select>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Button
                    size="small"
                    disabled={groupPage === 1 || tradingGroupsLoading}
                    onClick={() => setGroupPage((page) => Math.max(1, page - 1))}
                    sx={groupArrowBtnSx(groupPage === 1 || tradingGroupsLoading)}
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                  </Button>

                  {groupPageItems.map((item, index) => {
                    if (item === "…") {
                      return (
                        <Typography
                          key={`group-dots-${index}`}
                          sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}
                        >
                          …
                        </Typography>
                      );
                    }

                    const active = Number(item) === Number(groupPage);
                    return (
                      <Button
                        key={`group-page-${item}`}
                        size="small"
                        onClick={() => setGroupPage(Number(item))}
                        disabled={tradingGroupsLoading}
                        sx={{
                          minWidth: 28,
                          height: 28,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: active ? 900 : 700,
                          color: active ? "#111827" : "#6B7280",
                          backgroundColor: active ? "#EEF2FF" : "transparent",
                          "&:hover": {
                            backgroundColor: active ? "#EEF2FF" : "#F3F4F6",
                          },
                        }}
                      >
                        {item}
                      </Button>
                    );
                  })}

                  <Button
                    size="small"
                    disabled={groupPage === groupPageCount || tradingGroupsLoading}
                    onClick={() =>
                      setGroupPage((page) => Math.min(groupPageCount, page + 1))
                    }
                    sx={groupArrowBtnSx(groupPage === groupPageCount || tradingGroupsLoading)}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ===================== STEP 3 ===================== */}
        {activeStep === 2 && (
          <>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.5, mb: 2 }}>
              Distribute rebates and define allocation percentages.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Chip
                label={`Remaining share percentage: ${remainingStep3}%`}
                sx={{
                  px: 1.2,
                  py: 2.2,
                  borderRadius: 999,
                  background: "#EEF2F7",
                  color: "#111827",
                  fontWeight: 800,
                }}
              />
            </Box>

            <Box sx={{ ...innerPanelSx, borderRadius: 4 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 180px" },
                  gap: 2,
                  mb: 2,
                }}
              >
                    <Autocomplete
                      fullWidth
                      size="small"
                  options={ibOptions}
                  value={selectedSplitDraftOption}
                  loading={brokersLoading}
                  noOptionsText={brokersError || "No enabled IBs found."}
                  getOptionLabel={formatIbOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  onChange={(_, option) =>
                    setSplitDraft((p) => ({
                      ...p,
                      userId: option?.id ? String(option.id) : "",
                      userName: option?.name ?? "",
                    }))
                  }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="List of IBs"
                      placeholder="Search IB..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <TextField
                  size="small"
                  label="Shares %"
                  placeholder="--%"
                  value={splitDraft.shares}
                  onChange={(e) => setSplitDraft((p) => ({ ...p, shares: e.target.value }))}
                />
              </Box>

              <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1 }}>
                Set permissions:
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {[
                  { key: "showLink", label: "Show link" },
                  { key: "seeClients", label: "See clients" },
                  { key: "seeFull", label: "See full %" },
                ].map((p) => {
                  const checked = !!splitDraft[p.key];
                  return (
                    <Box
                      key={p.key}
                      onClick={() => toggleDraft(p.key)}
                      sx={{
                        cursor: "pointer",
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: checked ? "#EEF6FF" : "white",
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleDraft(p.key)}
                        onClick={(e) => e.stopPropagation()}
                        sx={greenCheckboxSx}
                      />
                      <Typography sx={{ fontSize: 12, color: "#374151", fontWeight: 700 }}>
                        {p.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleAddSplit}
                  disabled={!canAddSplit}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2,
                    background: canAddSplit ? "#DC2626" : "#E5E7EB",
                    color: canAddSplit ? "white" : "#111827",
                    "&:hover": { background: canAddSplit ? "#B91C1C" : "#D1D5DB" },
                  }}
                >
                  + Add
                </Button>
              </Box>

              {splits.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                    Added splits:
                  </Typography>

                  <TableContainer
                    sx={{
                      border: "1px solid #EEF2F7",
                      borderRadius: 3,
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        minWidth: splitTableMinWidth,
                        tableLayout: "fixed",
                        "& .MuiTableCell-root": {
                          fontFamily: '"Plus Jakarta Sans", sans-serif',
                        },
                      }}
                    >
                      <colgroup>
                        {SPLIT_TABLE_COLUMNS.map((column) => (
                          <col
                            key={column.field}
                            style={{
                              width: `${
                                splitTableColumnWidths[column.field] || column.width
                              }px`,
                            }}
                          />
                        ))}
                      </colgroup>
                      <TableHead>
                        <TableRow sx={{ background: "#F9FAFB" }}>
                          {SPLIT_TABLE_COLUMNS.map((column) => (
                            <TableCell
                              key={column.field}
                              align={column.align || "left"}
                              sx={{
                                ...splitTableColumnCellSx[column.field],
                                height: "25px",
                                paddingTop: 1.6,
                                paddingBottom: 1.6,
                                fontSize: TABLE_HEADER_FONT_SIZE,
                                fontWeight: 900,
                                color: "#374151",
                                position: "relative",
                                pr: "28px",
                                userSelect: "none",
                              }}
                            >
                              <Box
                                component="span"
                                title={column.label}
                                sx={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {column.label}
                              </Box>
                              <Box
                                component="span"
                                role="separator"
                                aria-label={`Resize ${column.label} column`}
                                aria-orientation="vertical"
                                aria-valuemin={column.minWidth}
                                aria-valuenow={
                                  splitTableColumnWidths[column.field] || column.width
                                }
                                tabIndex={0}
                                onPointerDown={(event) =>
                                  handleSplitTableColumnResizeStart(event, column)
                                }
                                onKeyDown={(event) =>
                                  handleSplitTableColumnResizeKeyDown(event, column)
                                }
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: "12px",
                                  height: "100%",
                                  cursor: "col-resize",
                                  touchAction: "none",
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    top: "22%",
                                    bottom: "22%",
                                    left: "50%",
                                    width: "2px",
                                    transform: "translateX(-50%)",
                                    borderRadius: "2px",
                                    backgroundColor: "rgba(21, 32, 42, 0.22)",
                                  },
                                  "&:hover::after, &:focus-visible::after": {
                                    backgroundColor: "#15202A",
                                  },
                                  "&:focus-visible": {
                                    outline: "2px solid #15202A",
                                    outlineOffset: "-2px",
                                  },
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {splits.map((s) => (
                          <TableRow key={s.rowId} hover>
                            <TableCell
                              title={String(s.userId)}
                              sx={{
                                ...splitTableColumnCellSx.subowner,
                                fontSize: 12,
                                color: "#111827",
                                fontWeight: 800,
                              }}
                            >
                              {String(s.userName || "").trim()
                                ? `${s.userName} (${s.userId})`
                                : s.userId}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                ...splitTableColumnCellSx.shares,
                                fontSize: 12,
                                color: "#111827",
                              }}
                            >
                              {s.shares}%
                            </TableCell>

                            <TableCell
                              align="center"
                              title={
                                [
                                  s.showLink ? "Show link" : null,
                                  s.seeClients ? "See clients" : null,
                                  s.seeFull ? "See full %" : null,
                                ]
                                  .filter(Boolean)
                                  .join(" • ") || "—"
                              }
                              sx={{
                                ...splitTableColumnCellSx.permissions,
                                fontSize: 11,
                                color: "#6B7280",
                              }}
                            >
                              {[
                                s.showLink ? "Show link" : null,
                                s.seeClients ? "See clients" : null,
                                s.seeFull ? "See full %" : null,
                              ]
                                .filter(Boolean)
                                .join(" • ") || "—"}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={splitTableColumnCellSx.action}
                            >
                              <IconButton
                                onClick={() => handleDeleteSplit(s.rowId)}
                                sx={{
                                  border: "1px solid #E5E7EB",
                                  borderRadius: 2,
                                  color: "#111827",
                                  "&:hover": { background: "#F9FAFB", borderColor: "#111827" },
                                }}
                                aria-label="delete split"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Footer Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (activeStep === 0 && subOwnersMode) {
                setSubOwnersMode(false);
                return;
              }
              if (activeStep === 0) navigate(-1);
              else handleBack();
            }}
            sx={blackBtnSx}
          >
            Back
          </Button>

          {activeStep < 2 ? (
            <Button
              variant="contained"
              disabled={
                isSubmitting ||
                (activeStep === 0 &&
                  (subOwnersMode ? !canContinueStep1 : !canContinueStep1Basic)) ||
                (activeStep === 1 && !canContinueStep2)
              }
              onClick={handleContinue}
              sx={addBtnSx(
                (activeStep === 0 && (subOwnersMode ? canContinueStep1 : canContinueStep1Basic)) ||
                  (activeStep === 1 && canContinueStep2)
              )}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={isSubmitting || !canCreateTreeStep3}
              onClick={handleCreateTree}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                backgroundColor: "#EB0000",
                "&:hover": {
                  backgroundColor: "#D50000",
                },
              }}
            >
              {isSubmitting ? "Creating..." : "Create Tree"}
            </Button>
          )}
        </Box>

        {submitError && (
          <Typography sx={{ mt: 1.5, fontSize: 12, color: "#B91C1C", fontWeight: 800 }}>
            {submitError}
          </Typography>
        )}
      </Box>

      {/* SUCCESS MODAL */}
      <Dialog
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            Tree created successfully
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            The broker tree has been created successfully and is now available in the system.
          </Typography>

          {createdTreeId ? (
            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 800, color: "#111827" }}>
              Tree ID: #{createdTreeId}
            </Typography>
          ) : null}

          <Box sx={{ mt: 3, display: "flex", gap: 1.2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setOpenSuccessModal(false);
                navigate(-1);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Back to Trees
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CreateTree;